import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Check, ShieldAlert, ArrowLeft, MessageCircle, FileText, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ProductCard } from "@/components/ProductCard";
import { getProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { buildTelegramLink } from "@/config";

const gbp = (n) => `£${n.toFixed(2)}`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setQty(1);
    setActiveImg(0);
    getProduct(id).then(setProduct).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <Skeleton className="aspect-square rounded-2xl bg-slate-100" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/3 bg-slate-100" />
            <Skeleton className="h-24 w-full bg-slate-100" />
            <Skeleton className="h-12 w-1/3 bg-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  const add = () => {
    const added = addItem(product, "product", qty);
    if (!added) return;
    toast.success(`${added} × ${product.name} added`, { description: "Lawful research & educational use only." });
    setOpen(true);
  };

  const gallery = product.gallery?.length ? product.gallery : [product.image];

  return (
    <div className="pt-8 pb-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-700">Home</Link><ChevronRight className="h-3.5 w-3.5" />
          <Link to="/shop" className="hover:text-slate-700">Research</Link><ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-700">{product.name}</span>
        </nav>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          {/* Gallery */}
          <div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="overflow-hidden rounded-2xl glass metallic-border">
              <img src={gallery[activeImg]} alt={product.name} className="aspect-square w-full object-cover" />
            </motion.div>
            {gallery.length > 1 && (
              <div className="mt-4 flex gap-3">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-20 overflow-hidden rounded-xl border transition-colors ${activeImg === i ? "border-slate-300" : "border-slate-200 hover:border-slate-300"}`}>
                    <img src={g} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono2 text-[11px] uppercase tracking-widest text-slate-500">{product.category}</span>
              {product.new_arrival && <span className="rounded-full bg-sky-500/15 px-2.5 py-0.5 text-[10px] font-mono2 uppercase tracking-wider text-sky-600 ring-1 ring-sky-500/30">New</span>}
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{product.name}</h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-700">{product.overview}</p>

            <div className="mt-6 flex items-center gap-4">
              <span className="font-display text-3xl font-bold metallic-text">{gbp(product.price)}</span>
              {product.compare_at && product.compare_at > product.price && (
                <>
                  <span className="text-xl text-slate-400 line-through">{gbp(product.compare_at)}</span>
                  <span className="rounded-full bg-rose-500/15 px-3 py-1 text-xs font-mono2 uppercase tracking-wider text-rose-600 ring-1 ring-rose-500/30">Sale</span>
                </>
              )}
              <span className="inline-flex items-center gap-1 text-sm text-emerald-600"><Check className="h-4 w-4" /> {product.status}</span>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center rounded-lg border border-slate-200 bg-white">
                <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 py-3 text-slate-700 hover:text-slate-900"><Minus className="h-4 w-4" /></button>
                <span data-testid="qty-value" className="w-10 text-center text-slate-900">{qty}</span>
                <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(5, q + 1))} disabled={qty >= 5} className="px-3.5 py-3 text-slate-700 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"><Plus className="h-4 w-4" /></button>
              </div>
              <Button data-testid="detail-add-to-cart" onClick={add} disabled={product.sold_out} className="btn-metallic h-12 flex-1 font-semibold">{product.sold_out ? "Sold out" : "Add to Cart"}</Button>
              <a href={buildTelegramLink(`Hello PepsWorld UK, I'm interested in: ${product.name} (${gbp(product.price)}).`)} target="_blank" rel="noreferrer">
                <Button variant="outline" className="h-12 w-full border-emerald-600/40 bg-transparent text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 sm:w-auto">
                  <MessageCircle className="mr-2 h-4 w-4" /> Enquire
                </Button>
              </a>
            </div>

            {/* Compliance */}
            <div className="mt-7 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <ShieldAlert className="h-5 w-5 shrink-0 text-amber-400" />
              <p className="text-xs leading-relaxed text-amber-900">
                <span className="font-semibold text-amber-700">Compliance Notice: </span>{product.legal_notice}
              </p>
            </div>

            {/* Specs */}
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold text-slate-900">Specifications</h3>
              <dl className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                {Object.entries(product.specifications || {}).map(([k, v], i) => (
                  <div key={k} className={`flex justify-between gap-4 px-4 py-3 text-sm ${i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="text-right text-slate-800">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        {/* Description + accordions */}
        <div className="mt-16 grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-slate-900">Research & Educational Description</h2>
            <p className="mt-4 leading-relaxed text-slate-600">{product.description}</p>

            <h3 className="mt-8 font-display text-lg font-semibold text-slate-900">Intended Lawful Use</h3>
            <p className="mt-2 leading-relaxed text-slate-600">{product.intended_use}</p>

            <Accordion type="single" collapsible className="mt-8 space-y-3">
              <AccordionItem value="storage" className="rounded-xl glass metallic-border px-5">
                <AccordionTrigger className="font-display text-base text-slate-900 hover:no-underline">Storage Information</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{product.storage}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="docs" className="rounded-xl glass metallic-border px-5">
                <AccordionTrigger className="font-display text-base text-slate-900 hover:no-underline">Testing & Documentation</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                  <div className="flex items-start gap-2">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
                    <span>{product.documentation}</span>
                  </div>
                </AccordionContent>
              </AccordionItem>
              {(product.faqs || []).map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl glass metallic-border px-5">
                  <AccordionTrigger className="font-display text-base text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl glass-strong metallic-border p-6">
              <h4 className="font-display text-base font-semibold text-slate-900">Safety & Legal Notice</h4>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Purchasers are responsible for ensuring that their intended use is lawful in the applicable jurisdiction and complies with all institutional requirements. Not for human or veterinary use.
              </p>
              <div className="mt-5 h-px w-full shimmer-line" />
              <Link to="/shop" className="mt-5 inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4" /> Back to catalogue
              </Link>
            </div>
          </aside>
        </div>

        {/* Related */}
        {product.related_products?.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display text-2xl font-bold text-slate-900">Related Products</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {product.related_products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
